import { useState } from "react";
import {
  Eye,
  FileText,
  Pencil,
  Terminal,
  Trash2,
  ChevronRight,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DiffView } from "./DiffView";

type Props = {
  toolName: string;
  content: string;
  filePath?: string;
  isResult?: boolean;
};

const toolIcons: Record<string, typeof Eye> = {
  Read: Eye,
  Write: FileText,
  Edit: Pencil,
  Bash: Terminal,
  Delete: Trash2,
};

function getToolIcon(toolName: string) {
  // Match common tool name patterns
  for (const [key, Icon] of Object.entries(toolIcons)) {
    if (toolName.toLowerCase().includes(key.toLowerCase())) {
      return Icon;
    }
  }
  return Wrench;
}

function isDiffContent(content: string): boolean {
  const lines = content.split("\n");
  return lines.some(
    (l) =>
      (l.startsWith("+") && !l.startsWith("+++")) ||
      (l.startsWith("-") && !l.startsWith("---"))
  );
}

export function ToolCallCard({ toolName, content, filePath, isResult }: Props) {
  const [expanded, setExpanded] = useState(false);
  const Icon = getToolIcon(toolName);
  const showDiff = isDiffContent(content);

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/50 overflow-hidden my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-zinc-800/50 transition-colors"
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 text-zinc-500 transition-transform shrink-0",
            expanded && "rotate-90"
          )}
        />
        <Icon className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
        <span className="text-xs font-medium text-zinc-300 truncate">
          {isResult ? `${toolName} result` : toolName}
        </span>
        {filePath && (
          <span className="text-xs text-zinc-500 truncate ml-auto">
            {filePath}
          </span>
        )}
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 max-h-80 overflow-y-auto">
          {showDiff ? (
            <DiffView content={content} />
          ) : (
            <pre className="text-xs text-zinc-400 p-3 whitespace-pre-wrap break-words font-mono">
              {content}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
